import { axiosClient } from '../../../library/Service/axiosClient';

const base = () => `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/reports`;

export const SCROLL_LIMIT = 25;

export type CampusOption = { id: number; name: string };

export type ReportField = {
    id: number;
    fieldKey: string;
    label: string;
    dataType: string;
    entityType: string;
    isSystem: number;
    sourceKind: string;
    showAsColumn: number;
    showAsFilter: number;
    sortOrder: number;
};

export type ReportRow = {
    id: number;
    userUuid: string;
    name: string | null;
    email: string | null;
    instituteId: number;
    instituteName: string | null;
    passOutYear: number | null;
    programName: string | null;
    eventName?: string | null;
    eventType?: string | null;
    role?: string | null;
    eventDate?: string | null;
    mode?: string | null;
    title?: string | null;
    awardingBody?: string | null;
    category?: string | null;
    level?: string | null;
    awardYear?: number | null;
    markers?: string[];
    custom?: Record<string, string>;
};

export type SearchPage = {
    rows: ReportRow[];
    limit: number;
    hasMore: boolean;
    lastId: number;
};

export type FilterChoice = { value: string; label: string };

export type FilterOptionSets = {
    options: Record<string, FilterChoice[]>;
    custom: Record<string, FilterChoice[]>;
};

export type ReportCounts = { events: number; rewards: number; markers: number };

export function reportCounts(instituteId: number | string) {
    return axiosClient().get<ReportCounts>(`${base()}/counts?instituteId=${instituteId}`);
}

export function campuses(instituteId?: number | string, q?: string) {
    const params = new URLSearchParams();
    if (instituteId) params.set('instituteId', String(instituteId));
    if (q) params.set('q', q);
    return axiosClient().get(`${base()}/campuses?${params.toString()}`);
}

export type ReportKind = 'events' | 'rewards' | 'markers';

export function fields(reportType: ReportKind, instituteId?: number | string | null) {
    const params = new URLSearchParams();
    if (instituteId) params.set('instituteId', String(instituteId));
    const query = params.toString();
    return axiosClient().get(`${base()}/${reportType}/fields${query ? `?${query}` : ''}`);
}

export async function downloadReport(
    reportType: ReportKind,
    body: {
        instituteId?: number | string | null;
        q?: string;
        filters?: Record<string, string[] | Record<string, string[]>>;
    }
) {
    const response = await axiosClient().post(`${base()}/${reportType}/export`, body, { responseType: 'blob' });
    const blob = response.data as Blob;
    const type = String(response.headers?.['content-type'] || '');
    if (type.includes('application/json')) {
        const parsed = JSON.parse(await blob.text());
        throw { isError: true, message: parsed.message || 'Export failed.', data: parsed };
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportType}-export.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export function uploadReportFile(reportType: ReportKind, instituteId: number | string, file: File) {
    const body = new FormData();
    body.append('instituteId', String(instituteId));
    body.append('file', file);
    return axiosClient().post(`${base()}/${reportType}/upload`, body);
}

export function filterOptions(
    reportType: ReportKind,
    instituteId?: number | string | null,
    extra?: { suggest?: string; q?: string }
) {
    const params = new URLSearchParams();
    if (instituteId) params.set('instituteId', String(instituteId));
    if (extra?.suggest) params.set('suggest', extra.suggest);
    if (extra?.q) params.set('q', extra.q);
    const query = params.toString();
    return axiosClient().get(`${base()}/${reportType}/filter-options${query ? `?${query}` : ''}`);
}

export function search(
    reportType: ReportKind,
    body: {
        instituteId?: number | string | null;
        lastId?: number;
        q?: string;
        filters?: Record<string, string[] | Record<string, string[]>>;
    }
) {
    return axiosClient().post(`${base()}/${reportType}/search`, body);
}

export function createField(
    reportType: ReportKind,
    body: { instituteId: number | string; label: string; dataType: string; visibility: string; optionsText?: string; markerType?: string }
) {
    return axiosClient().post(`${base()}/${reportType}/fields`, body);
}

export function updateField(
    reportType: ReportKind,
    id: number,
    body: { instituteId: number | string; showAsColumn?: boolean; showAsFilter?: boolean }
) {
    return axiosClient().patch(`${base()}/${reportType}/fields/${id}`, body);
}
